import type { ResourcePickerResultFields } from 'v5/services/front/UserPreferencesService/UserPreferencesService';

/** More would not fit one line of a row. */
export const MAX_LINE_FIELDS = 3;

/** List fields whose values read as badges. */
export const BADGE_FIELDS = ['locations', 'tags', 'groups'] as const;
export type BadgeField = (typeof BADGE_FIELDS)[number];

export const isBadgeField = (key: string): key is BadgeField =>
  (BADGE_FIELDS as ReadonlyArray<string>).includes(key);

/** What a row can show on its right, in the order it shows them: the strip of pictures last. */
export const RIGHT_FIELDS = [...BADGE_FIELDS, 'pictures'] as const;

export const resultFieldsUtils = {
  /** A picked field goes at the end of the line while there is room. */
  add: (
    fields: ResourcePickerResultFields,
    key: string
  ): ResourcePickerResultFields =>
    fields.line.includes(key) || fields.line.length >= MAX_LINE_FIELDS
      ? fields
      : { ...fields, line: [...fields.line, key] },
  remove: (
    fields: ResourcePickerResultFields,
    key: string
  ): ResourcePickerResultFields => ({
    ...fields,
    line: fields.line.filter(field => field !== key)
  }),
  reorder: (
    fields: ResourcePickerResultFields,
    line: ReadonlyArray<string>
  ): ResourcePickerResultFields => ({ ...fields, line: [...line] }),
  /** What shows on the right, kept in the row's order whatever order it was ticked in. */
  setRight: (
    fields: ResourcePickerResultFields,
    shown: ReadonlySet<string>
  ): ResourcePickerResultFields => ({
    ...fields,
    right: RIGHT_FIELDS.filter(field => shown.has(field))
  })
};

/** A list's first value, and how many more ("Gdańsk +2"). */
export function firstAndMore(values: ReadonlyArray<string>): string | null {
  const [first, ...rest] = values;
  if (first === undefined) return null;
  return rest.length > 0 ? `${first} +${rest.length}` : first;
}

/**
 * The line under a result's name: each field's text in order, empty ones left out, except role
 * and rate, which say they are missing (`placeholderOf`), as the row always did.
 */
export function resultLine(
  line: ReadonlyArray<string>,
  textOf: (key: string) => string | null,
  placeholderOf: (key: string) => string | null
): string {
  return line
    .map(key => textOf(key) ?? placeholderOf(key))
    .filter((text): text is string => text !== null && text !== '')
    .join(' · ');
}
